import React, { useState, useEffect } from 'react'
import { X, Calendar, Clock, User, Tag, AlertCircle, Edit, Trash2, Save } from 'lucide-react'
import { templateAPI } from '../../services/api'

const EventDetailsModal = ({ 
  isOpen, 
  onClose, 
  event = null,
  onEventUpdated,
  onEventDeleted,
  templates = [] 
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    end_date: '',
    event_type: 'design_deadline',
    priority: 'medium',
    status: 'pending',
    template_id: '',
    assigned_to: '',
    reminder_enabled: true,
    reminder_minutes: '15,60',
    is_recurring: false,
    recurring_pattern: '',
    recurring_end_date: '',
    color: '#3B82F6'
  })

  // Reset formData when event changes
  useEffect(() => {
    if (event) {
      const resetFormData = () => ({
        title: event.title || '',
        description: event.description || '',
        event_date: event.event_date ? new Date(event.event_date).toISOString().slice(0, 16) : '',
        end_date: event.end_date ? new Date(event.end_date).toISOString().slice(0, 16) : '',
        event_type: event.event_type || 'design_deadline',
        priority: event.priority || 'medium',
        status: event.status || 'pending',
        template_id: event.template_id || '',
        assigned_to: event.assigned_to || '',
        reminder_enabled: event.reminder_enabled !== false,
        reminder_minutes: event.reminder_minutes || '15,60',
        is_recurring: event.is_recurring || false,
        recurring_pattern: event.recurring_pattern || '',
        recurring_end_date: event.recurring_end_date ? new Date(event.recurring_end_date).toISOString().slice(0, 10) : '',
        color: event.color || '#3B82F6'
      })
      setFormData(resetFormData())
    }
  }, [event])

  // Reset formData when entering edit mode to ensure original values are preserved
  useEffect(() => {
    if (isEditing && event) {
      setFormData({
        title: event.title || '',
        description: event.description || '',
        event_date: event.event_date ? new Date(event.event_date).toISOString().slice(0, 16) : '',
        end_date: event.end_date ? new Date(event.end_date).toISOString().slice(0, 16) : '',
        event_type: event.event_type || 'design_deadline',
        priority: event.priority || 'medium',
        status: event.status || 'pending',
        template_id: event.template_id || '',
        assigned_to: event.assigned_to || '',
        reminder_enabled: event.reminder_enabled !== false,
        reminder_minutes: event.reminder_minutes || '15,60',
        is_recurring: event.is_recurring || false,
        recurring_pattern: event.recurring_pattern || '',
        recurring_end_date: event.recurring_end_date ? new Date(event.recurring_end_date).toISOString().slice(0, 10) : '',
        color: event.color || '#3B82F6'
      })
    }
  }, [isEditing, event])

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSave = async () => {
    if (!event?.id) return

    setLoading(true)
    setError(null)

    try {
      const eventData = {
        ...formData,
        event_date: new Date(formData.event_date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        recurring_end_date: formData.recurring_end_date ? new Date(formData.recurring_end_date).toISOString() : null,
        template_id: formData.template_id || null,
        assigned_to: formData.assigned_to || null,
        reminder_minutes: formData.reminder_enabled ? formData.reminder_minutes : null
      }

      const response = await templateAPI.updateCalendarEvent(event.id, eventData)
      
      if (response.data) {
        onEventUpdated?.(response.data)
        setIsEditing(false)
      }
    } catch (err) {
      console.error('Error updating event:', err)
      setError(err.response?.data?.detail || 'Failed to update event')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!event?.id || !window.confirm('Are you sure you want to delete this event?')) return

    setLoading(true)
    setError(null)

    try {
      await templateAPI.deleteCalendarEvent(event.id)
      onEventDeleted?.(event.id)
      onClose()
    } catch (err) {
      console.error('Error deleting event:', err)
      setError(err.response?.data?.detail || 'Failed to delete event')
    } finally {
      setLoading(false)
    }
  }

  const getEventTypeIcon = (type) => {
    const icons = {
      design_deadline: '🎨',
      review_deadline: '👀',
      client_meeting: '🤝',
      project_launch: '🚀',
      content_creation: '📝',
      social_media_post: '📱',
      campaign_start: '📢',
      campaign_end: '🏁',
      other: '📅'
    }
    return icons[type] || '📅'
  }

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-green-600 bg-green-50',
      medium: 'text-yellow-600 bg-yellow-50',
      high: 'text-orange-600 bg-orange-50',
      urgent: 'text-red-600 bg-red-50'
    }
    return colors[priority] || 'text-gray-600 bg-gray-50'
  }

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-600 bg-yellow-50',
      in_progress: 'text-blue-600 bg-blue-50',
      completed: 'text-green-600 bg-green-50',
      cancelled: 'text-red-600 bg-red-50'
    }
    return colors[status] || 'text-gray-600 bg-gray-50'
  }

  if (!isOpen || !event) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">{getEventTypeIcon(event.event_type)}</span>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {isEditing ? 'Edit Event' : 'Event Details'}
              </h2>
              <p className="text-sm text-gray-500">
                {new Date(event.event_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {!isEditing && (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  title="Edit event"
                >
                  <Edit className="w-5 h-5" />
                </button>
                <button
                  onClick={handleDelete}
                  className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                  title="Delete event"
                  disabled={loading}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Event Title
            </label>
            {isEditing ? (
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter event title"
              />
            ) : (
              <p className="text-gray-900 font-medium">{event.title}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            {isEditing ? (
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter event description"
              />
            ) : (
              <p className="text-gray-700">{event.description || 'No description'}</p>
            )}
          </div>

          {/* Event Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Event Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Event Type
              </label>
              {isEditing ? (
                <select
                  value={formData.event_type}
                  onChange={(e) => handleInputChange('event_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="design_deadline">Design Deadline</option>
                  <option value="review_deadline">Review Deadline</option>
                  <option value="client_meeting">Client Meeting</option>
                  <option value="project_launch">Project Launch</option>
                  <option value="content_creation">Content Creation</option>
                  <option value="social_media_post">Social Media Post</option>
                  <option value="campaign_start">Campaign Start</option>
                  <option value="campaign_end">Campaign End</option>
                  <option value="other">Other</option>
                </select>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {getEventTypeIcon(event.event_type)} {event.event_type.replace('_', ' ').toUpperCase()}
                </span>
              )}
            </div>

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              {isEditing ? (
                <select
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              ) : (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(event.priority)}`}>
                  {event.priority.toUpperCase()}
                </span>
              )}
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              {isEditing ? (
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              ) : (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(event.status)}`}>
                  {event.status.replace('_', ' ').toUpperCase()}
                </span>
              )}
            </div>

            {/* Template */}
            {templates.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Related Template
                </label>
                {isEditing ? (
                  <select
                    value={formData.template_id}
                    onChange={(e) => handleInputChange('template_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">No template</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-gray-700">
                    {event.template_id ? 
                      templates.find(t => t.id === event.template_id)?.name || 'Unknown template' : 
                      'No template'
                    }
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date & Time
              </label>
              {isEditing ? (
                <input
                  type="datetime-local"
                  value={formData.event_date}
                  onChange={(e) => handleInputChange('event_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              ) : (
                <p className="text-gray-700">
                  {new Date(event.event_date).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
                </p>
              )}
            </div>

            {event.end_date && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date & Time
                </label>
                {isEditing ? (
                  <input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => handleInputChange('end_date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                ) : (
                  <p className="text-gray-700">
                    {new Date(event.end_date).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        {isEditing && (
          <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
            <button
              onClick={() => {
                setIsEditing(false)
                setError(null)
                // Reset formData to original event values when canceling
                if (event) {
                  setFormData({
                    title: event.title || '',
                    description: event.description || '',
                    event_date: event.event_date ? new Date(event.event_date).toISOString().slice(0, 16) : '',
                    end_date: event.end_date ? new Date(event.end_date).toISOString().slice(0, 16) : '',
                    event_type: event.event_type || 'design_deadline',
                    priority: event.priority || 'medium',
                    status: event.status || 'pending',
                    template_id: event.template_id || '',
                    assigned_to: event.assigned_to || '',
                    reminder_enabled: event.reminder_enabled !== false,
                    reminder_minutes: event.reminder_minutes || '15,60',
                    is_recurring: event.is_recurring || false,
                    recurring_pattern: event.recurring_pattern || '',
                    recurring_end_date: event.recurring_end_date ? new Date(event.recurring_end_date).toISOString().slice(0, 10) : '',
                    color: event.color || '#3B82F6'
                  })
                }
              }}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading || !formData.title.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>{loading ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default EventDetailsModal
